import apiClient from '../../../services/api-client'
import type { ListTenantsParams, ListTenantsResponse } from '../types/tenant.types'

export interface TenantLicense {
  EmpCount?: number
  EmpPsgCount?: number
  EmpLveCount?: number
  EmpClaCount?: number
  EmpAttCount?: number
  EmpPfmCount?: number
}

export interface CreateTenantRequest {
  tenantCode?: string
  tenantCodePrefix?: string
  workspaceType?: 'new' | 'demo' | 'trial' | 'uat'
  partnerCode?: string
  tenantName: string
  workspaceCode: string
  countryCode: string // Note: singular in backend
  databaseGroupCode: string
  databaseServerCode?: string
  featureFlags: string[]
  restoreFromTemplate: string | null
  timeZoneId: string
  isDemo: boolean | null
  inactiveDate: string | null
  licenseJsonb?: string
}

export interface DatabaseServer {
  id: string
  serverCode: string
  serverHost: string
  serverPort: number
  dbCreatorUser: string
  isDefault: boolean
  isActive: boolean
}

export async function listTenants(params?: ListTenantsParams): Promise<ListTenantsResponse> {
  const response = await apiClient.get<ListTenantsResponse>('/tenant', {
    params: {
      includeInactive: params?.includeInactive ?? false,
      includeFeatureFlags: params?.includeFeatureFlags ?? true,
      includeUserCount: params?.includeUserCount ?? false
    }
  })
  return response.data
}

// Lightweight active-tenant list for dropdowns, accessible to workspace users and requesters
// (backend GET /tenant/lookup is guarded by WorkspaceRequesterRole, unlike GET /tenant).
export async function lookupTenants(params?: { includeFeatureFlags?: boolean }): Promise<ListTenantsResponse> {
  const response = await apiClient.get<ListTenantsResponse>('/tenant/lookup', {
    params: {
      includeFeatureFlags: params?.includeFeatureFlags ?? false
    }
  })
  return response.data
}

export interface UpdateTenantRequest {
  workspaceCode: string
  tenantName: string
  countryCodes: string
  timeZoneId: string
  isDemo: boolean
  inactiveDate: string | null
  destroyDate: string | null
  featureFlags: string[]
  licenseJsonb?: string
}

export interface CreateTenantResponse {
  tenantCode: string
  message: string
}

export async function createTenant(request: CreateTenantRequest): Promise<CreateTenantResponse> {
  const response = await apiClient.post<CreateTenantResponse>('/tenant', request)
  return response.data
}

export async function updateTenant(tenantCode: string, request: UpdateTenantRequest): Promise<void> {
  await apiClient.post(`/tenant/${tenantCode}`, request)
}

export async function destroyTenant(tenantCode: string, passCode: string): Promise<void> {
  await apiClient.post('/tenant/destroy', { tenantCode, passCode })
}

export async function getDatabaseGroupCodes(): Promise<string[]> {
  const response = await apiClient.get<{ data: string[] }>('/database/group-codes')
  return response.data.data
}

export async function getDatabaseServers(): Promise<DatabaseServer[]> {
  const response = await apiClient.get<{ data: DatabaseServer[] }>('/database-server')
  return response.data.data
}

export async function setupUpdate(tenantCode: string): Promise<void> {
  await apiClient.post('/tenant/setup/update', { tenantCode })
}

export async function setupSeeds(tenantCode: string): Promise<void> {
  await apiClient.post('/tenant/setup/seeds', { tenantCode })
}
